import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Heart, Award, Users, Camera } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

import gallery4 from "@/assets/gallery-4.jpg";

const values = [
  {
    icon: Heart,
    title: "Passion for Storytelling",
    description: "We believe every couple has a unique love story worth telling beautifully.",
  },
  {
    icon: Award,
    title: "Quality Without Compromise",
    description: "Luxury-level photography and cinematography at prices real couples can afford.",
  },
  {
    icon: Users,
    title: "Client-First Approach",
    description: "Your vision drives everything we do. We listen, adapt, and deliver beyond expectations.",
  },
  {
    icon: Camera,
    title: "Modern & Timeless",
    description: "A blend of contemporary style with classic elegance that never goes out of fashion.",
  },
];

const About = () => {
  const [heroImage, setHeroImage] = useState({ url: gallery4, alt: "Happy wedding couple laughing together" });

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const { data: locations, error: locError } = await supabase
          .from("portfolio_media_locations")
          .select("media_id")
          .eq("location", "about_page");

        if (locError) throw locError;

        if (locations && locations.length > 0) {
          const mediaIds = locations.map((l) => l.media_id);
          
          const { data: mediaData, error: mediaError } = await supabase
            .from("portfolio_media")
            .select("*")
            .in("id", mediaIds)
            .eq("is_active", true)
            .eq("media_type", "image")
            .order("sort_order", { ascending: true })
            .limit(1);

          if (mediaError) throw mediaError;

          if (mediaData && mediaData.length > 0) {
            setHeroImage({
              url: mediaData[0].url,
              alt: mediaData[0].alt_text || mediaData[0].title,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching about page media:", error);
      }
    };

    fetchMedia();
  }, []);

  return (
    <>
      <Helmet>
        <title>About Pixie Clicks | Affordable Wedding Photography | NJ, FL & CO</title>
        <meta
          name="description"
          content="Learn about Pixie Clicks and our mission to make beautiful wedding photography affordable. Trusted by 200+ couples in NJ, FL & CO."
        />
        <meta property="og:title" content="About Pixie Clicks | Affordable Wedding Photography" />
        <meta property="og:description" content="Learn about our mission to make beautiful wedding photography affordable. Trusted by 200+ couples." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/about-pixie-clicks" />
        <link rel="canonical" href="https://pixieclicks.com/about-pixie-clicks" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Pixie Clicks",
            "description": "Affordable wedding photography and cinematography services.",
            "areaServed": ["New Jersey", "Florida", "Colorado"],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "200"
            }
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="relative py-20 md:py-32 bg-muted/30">
          <div className="container-wide">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="font-serif text-4xl font-semibold leading-tight text-foreground md:text-5xl">
                  We Believe Every Love Story Deserves to be{" "}
                  <span className="text-primary">Beautifully Captured</span>
                </h1>
                <p className="mt-6 text-lg text-muted-foreground">
                  Pixie Clicks was founded with a simple mission: to make stunning wedding photography and cinematography accessible to every couple, regardless of budget.
                </p>
                <p className="mt-4 text-muted-foreground">
                  We're not about exclusivity or luxury price tags. We're about capturing authentic moments, genuine emotions, and the unique magic of your love story — all while respecting your budget.
                </p>
                <Button variant="hero" size="lg" className="mt-8" asChild>
                  <Link to="/contact">Check Your Date</Link>
                </Button>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <img
                  src={heroImage.url}
                  alt={heroImage.alt}
                  className="rounded-2xl shadow-elevated"
                />
                <div className="absolute -bottom-6 -left-6 rounded-xl bg-primary p-6 text-primary-foreground shadow-rose">
                  <p className="text-3xl font-bold">200+</p>
                  <p className="text-sm">Weddings Captured</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="section-padding">
          <div className="container-wide">
            <div className="text-center">
              <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
                What Makes Us Different
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
                We're not your typical wedding photographers. Here's what sets Pixie Clicks apart.
              </p>
            </div>

            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <value.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-foreground">
                    {value.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Trust Us */}
        <section className="section-padding bg-muted/30">
          <div className="container-narrow text-center">
            <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Why Couples Trust Pixie Clicks
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              <div className="rounded-xl bg-card border border-border p-6">
                <p className="text-3xl font-bold text-primary">4.9★</p>
                <p className="mt-2 text-muted-foreground">Average rating from 200+ reviews</p>
              </div>
              <div className="rounded-xl bg-card border border-border p-6">
                <p className="text-3xl font-bold text-primary">100%</p>
                <p className="mt-2 text-muted-foreground">On-time delivery guarantee</p>
              </div>
              <div className="rounded-xl bg-card border border-border p-6">
                <p className="text-3xl font-bold text-primary">5+ Years</p>
                <p className="mt-2 text-muted-foreground">Capturing love stories</p>
              </div>
            </div>
            <Button variant="hero" size="lg" className="mt-10" asChild>
              <Link to="/contact">Start Your Journey</Link>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default About;
